import React, { useState, useRef } from 'react';
import { 
  Briefcase, ChevronDown, ChevronLeft, ChevronRight, 
  Upload, XCircle, X, FileText, 
  ExternalLink, Loader2, Eye, Download, Check, Save,
  Plus, Trash2, Star, Ban
} from 'lucide-react';
import { Button } from '../../ui/button';
import { ASSET_CONFIGS, getActualValue } from './assetConfigs';

const PrivateInvestmentDetailModal = React.memo(({ 
  item, 
  items, 
  currentIndex, 
  onClose, 
  onSave, 
  onNavigate,
  formatCurrency,
  API_URL,
  userEmail,
  isNewItem = false
}) => {
  const getActualValueLocal = (i) => {
    if (i.currentPrice && i.currentPrice > 0) return i.currentPrice;
    if (i.shares && i.shares > 1) return i.shares;
    return i.currentPrice || 0;
  };
  
  const actualValue = getActualValueLocal(item);
  const actualCost = item.costBasis || 0;
  
  const [editForm, setEditForm] = useState({ 
    ...item,
    shares: actualValue
  });
  const [documents, setDocuments] = useState(item.documents || []);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [saving, setSaving] = useState(false);
  const docInputRef = useRef(null);
  const [viewingDocument, setViewingDocument] = useState(null);
  
  // Section collapse states
  const initialCollapseState = useRef(null);
  if (initialCollapseState.current === null) {
    try {
      const saved = localStorage.getItem('privateInvestmentModalCollapseState');
      initialCollapseState.current = saved ? JSON.parse(saved) : { details: true, documents: false };
    } catch (e) {
      initialCollapseState.current = { details: true, documents: false };
    }
  }
  
  const [collapseState, setCollapseState] = useState(initialCollapseState.current);
  
  const updateCollapseState = (key, value) => {
    setCollapseState(prev => {
      const newState = { ...prev, [key]: value };
      setTimeout(() => {
        try { localStorage.setItem('privateInvestmentModalCollapseState', JSON.stringify(newState)); } catch { /* ignore */ }
      }, 100);
      return newState;
    });
  };
  
  const detailsExpanded = collapseState.details;
  const documentsExpanded = collapseState.documents;
  
  const setDetailsExpanded = (val) => updateCollapseState('details', val);
  const setDocumentsExpanded = (val) => updateCollapseState('documents', val);

  React.useEffect(() => {
    const newValue = getActualValueLocal(item);
    setEditForm({ ...item, shares: newValue });
    setDocuments(item.documents || []);
    setViewingDocument(null);
  }, [item.id]);

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < items.length - 1;

  const handleDocumentUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingDoc(true);
    const newDocs = [...documents];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'portfolio_private_investment_docs');
        const response = await fetch(`${API_URL}/api/upload/image`, { method: 'POST', body: formData });
        if (response.ok) {
          const data = await response.json();
          newDocs.push({ name: file.name, url: data.url, uploadedAt: new Date().toISOString() });
        }
      } catch (error) { console.error('Document upload error:', error); }
    }
    setDocuments(newDocs);
    setUploadingDoc(false);
    if (docInputRef.current) docInputRef.current.value = '';
  };

  const handleDeleteDocument = (index) => setDocuments(documents.filter((_, i) => i !== index));

  const handleSave = async (closeAfterSave = false) => {
    if (saving) return;
    setSaving(true);
    try {
      const enteredValue = parseFloat(editForm.shares) || 0;
      const updateData = { ...editForm, currentPrice: enteredValue, shares: 1, documents };
      await onSave(item.id, updateData, closeAfterSave);
    } finally {
      setSaving(false);
    }
  };

  const displayName = ASSET_CONFIGS['PRIVATE INVESTMENTS']?.getDisplayName?.(item) || item.symbol || item.name || 'Investment';
  const returnAmount = actualValue - actualCost;
  const returnPercent = actualCost > 0 ? ((returnAmount / actualCost) * 100) : 0;

  return (
    <div 
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" 
      onClick={isNewItem ? undefined : onClose}
      data-testid="private-investment-detail-modal"
    >
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-4 rounded-t-xl z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {!isNewItem && (
                <button 
                  onClick={() => onNavigate('prev')} 
                  disabled={!canGoPrev} 
                  className={`p-1 rounded-full ${canGoPrev ? 'hover:bg-white/20' : 'opacity-30 cursor-not-allowed'}`}
                  data-testid="private-investment-nav-prev"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}
              <div>
                <h2 className="text-xl font-bold" data-testid="private-investment-modal-title">
                  {isNewItem ? 'Add New Investment' : displayName}
                </h2>
                {!isNewItem && item.platform && <p className="text-indigo-200 text-sm">{item.platform}</p>}
                {!isNewItem && <p className="text-indigo-200 text-xs">Item {currentIndex + 1} of {items.length}</p>}
              </div>
              {!isNewItem && (
                <button 
                  onClick={() => onNavigate('next')} 
                  disabled={!canGoNext} 
                  className={`p-1 rounded-full ${canGoNext ? 'hover:bg-white/20' : 'opacity-30 cursor-not-allowed'}`}
                  data-testid="private-investment-nav-next"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-4">
              {!isNewItem && (
                <div className="text-right">
                  <div className="text-2xl font-bold" data-testid="private-investment-value">{formatCurrency(actualValue)}</div>
                  <div className="text-indigo-200 text-sm">
                    Invested: {formatCurrency(actualCost)} | Return: <span className={returnAmount >= 0 ? 'text-emerald-300' : 'text-red-300'}>{returnAmount >= 0 ? '+' : ''}{formatCurrency(Math.abs(returnAmount))} ({returnPercent.toFixed(1)}%)</span>
                  </div>
                </div>
              )}
              {/* Sold/Disposed Toggle */}
              {!isNewItem && (
              <button
                type="button"
                data-testid="disposed-toggle"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditForm(prev => ({ ...prev, disposed: !prev.disposed })); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer ${editForm.disposed ? 'bg-red-500 text-white hover:bg-red-400' : 'bg-white/20 text-white hover:bg-white/30'}`}
                title={editForm.disposed ? 'Mark as active' : 'Mark as sold/disposed'}
              >
                <Ban className="w-4 h-4" /><span>{editForm.disposed ? 'Sold' : 'Active'}</span>
              </button>
              )}
              {/* Complete Toggle */}
              {!isNewItem && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditForm(prev => ({ ...prev, isComplete: !prev.isComplete }));
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer ${
                    editForm.isComplete
                      ? 'bg-amber-400 text-amber-900 hover:bg-amber-300'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  title={editForm.isComplete ? 'Mark as incomplete' : 'Mark as complete'}
                  data-testid="complete-toggle"
                >
                  <Star className={`w-4 h-4 ${editForm.isComplete ? 'fill-amber-900' : ''}`} />
                  <span>{editForm.isComplete ? 'Complete' : 'Incomplete'}</span>
                </button>
              )}
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full" data-testid="private-investment-close">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={`p-6 space-y-6 ${editForm.disposed ? 'bg-gray-100 asset-sold' : ''}`}>
          {/* Investment Details */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button onClick={() => setDetailsExpanded(!detailsExpanded)} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors" data-testid="details-section-toggle">
              <span className="font-semibold text-slate-700 flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Investment Details
              </span>
              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${detailsExpanded ? 'rotate-180' : ''}`} />
            </button>
            {detailsExpanded && (
              <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Company/Investment Name</label>
                  <input 
                    type="text" 
                    value={editForm.symbol || ''} 
                    onChange={(e) => setEditForm({...editForm, symbol: e.target.value})} 
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    data-testid="investment-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Investment Type</label>
                  <select 
                    value={editForm.platform || ''} 
                    onChange={(e) => setEditForm({...editForm, platform: e.target.value})} 
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    data-testid="investment-type-select"
                  >
                    <option value="">Select Type</option>
                    <option value="Private Equity">Private Equity</option>
                    <option value="Startup">Startup</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Syndication">Syndication</option>
                    <option value="Loan">Loan</option>
                    <option value="Note">Note</option>
                    <option value="Promissory Note">Promissory Note</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Status</label>
                  <select 
                    value={editForm.investmentStatus || ''} 
                    onChange={(e) => setEditForm({...editForm, investmentStatus: e.target.value})} 
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    data-testid="investment-status-select"
                  >
                    <option value="">Select Status</option>
                    <option value="Active">Active</option>
                    <option value="Matured">Matured</option>
                    <option value="Defaulted">Defaulted</option>
                    <option value="Exited">Exited</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Contact Person</label>
                  <input 
                    type="text" 
                    value={editForm.contactPerson || ''} 
                    onChange={(e) => setEditForm({...editForm, contactPerson: e.target.value})} 
                    placeholder="Name or company"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    data-testid="contact-person-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Interest Rate (%)</label>
                  <input 
                    type="text" 
                    value={editForm.interestRate || ''} 
                    onChange={(e) => setEditForm({...editForm, interestRate: e.target.value})} 
                    placeholder="e.g. 8.5"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    data-testid="interest-rate-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Investment Date</label>
                  <input 
                    type="date" 
                    value={editForm.investmentDate || ''} 
                    onChange={(e) => setEditForm({...editForm, investmentDate: e.target.value})} 
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    data-testid="investment-date-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Maturity Date</label>
                  <input 
                    type="date" 
                    value={editForm.maturityDate || ''} 
                    onChange={(e) => setEditForm({...editForm, maturityDate: e.target.value})} 
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    data-testid="maturity-date-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Investment Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <input 
                      type="text" 
                      value={editForm.costBasis ? Math.round(editForm.costBasis).toLocaleString() : ''} 
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        setEditForm({...editForm, costBasis: parseInt(val) || 0});
                      }} 
                      className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      data-testid="investment-amount-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Current/Estimated Value</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <input 
                      type="text" 
                      value={editForm.shares ? Math.round(editForm.shares).toLocaleString() : ''} 
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        setEditForm({...editForm, shares: parseInt(val) || 0});
                      }} 
                      className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      data-testid="current-value-input"
                    />
                  </div>
                </div>
                <div className="col-span-2 md:col-span-3">
                  <label className="block text-xs text-slate-500 mb-1">Terms</label>
                  <textarea 
                    value={editForm.terms || ''} 
                    onChange={(e) => setEditForm({...editForm, terms: e.target.value})} 
                    rows={2} 
                    placeholder="Payment schedule, conditions, etc."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    data-testid="terms-textarea"
                  />
                </div>
                <div className="col-span-2 md:col-span-3">
                  <label className="block text-xs text-slate-500 mb-1">Notes</label>
                  <textarea 
                    value={editForm.notes || ''} 
                    onChange={(e) => setEditForm({...editForm, notes: e.target.value})} 
                    rows={2} 
                    placeholder="Additional notes..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    data-testid="notes-textarea"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button onClick={() => setDocumentsExpanded(!documentsExpanded)} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors" data-testid="documents-section-toggle">
              <span className="font-semibold text-slate-700 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Documents
                {documents.length > 0 && <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full">{documents.length}</span>}
              </span>
              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${documentsExpanded ? 'rotate-180' : ''}`} />
            </button>
            {documentsExpanded && (
              <div className="p-4">
                <div className="space-y-2">
                  {documents.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-indigo-600" />
                        <span className="text-sm text-slate-700">{doc.name}</span>
                        {doc.uploadedAt && <span className="text-xs text-slate-400">Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setViewingDocument(doc); }} className="p-1.5 hover:bg-indigo-100 rounded text-indigo-600" title="View"><Eye className="w-4 h-4" /></button>
                        <a href={doc.url} download={doc.name || 'document'} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-slate-200 rounded text-slate-600" title="Download"><Download className="w-4 h-4" /></a>
                        <button onClick={() => handleDeleteDocument(idx)} className="p-1.5 hover:bg-red-100 rounded text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <label className="mt-3 flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors">
                  <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx" multiple onChange={handleDocumentUpload} className="hidden" disabled={uploadingDoc} />
                  {uploadingDoc ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
                      <span className="text-sm text-indigo-600">Uploading document...</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-slate-400" />
                      <span className="text-sm text-slate-500">Upload Documents (Contracts, Agreements, Statements)</span>
                    </>
                  )}
                </label>
              </div>
            )}
          </div>

          {/* PDF Viewer */}
          {viewingDocument && (
            <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setViewingDocument(null)}>
              <div className="bg-white rounded-xl w-full max-w-5xl h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-indigo-100">
                      <FileText className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{viewingDocument.name}</h3>
                      <p className="text-xs text-slate-500">Document</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={viewingDocument.url} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Open in New Tab">
                      <ExternalLink className="w-5 h-5" />
                    </a>
                    <a href={viewingDocument.url} download={viewingDocument.name || 'document'} className="p-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Download">
                      <Download className="w-5 h-5" />
                    </a>
                    <button onClick={() => setViewingDocument(null)} className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors" title="Close">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 bg-slate-100 rounded-b-xl overflow-hidden relative">
                  <iframe
                    src={`${API_URL}/api/pdf-proxy?url=${encodeURIComponent(viewingDocument.url)}`}
                    className="w-full h-full border-0"
                    title={viewingDocument.name}
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-slate-800/90 text-white text-xs p-2">
                    <div className="flex items-center justify-between">
                      <span>If document doesn&apos;t load:</span>
                      <div className="flex items-center gap-2">
                        <button onClick={() => navigator.clipboard.writeText(viewingDocument.url)} className="text-slate-400 hover:text-white" title="Copy URL">Copy URL</button>
                        <a href={viewingDocument.url} target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:text-indigo-200 underline">Open directly</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 p-4 flex justify-end gap-3 rounded-b-xl">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="private-investment-cancel">Cancel</Button>
          <Button 
            onClick={() => handleSave(false)} 
            disabled={saving || uploadingDoc} 
            className="bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-50"
            data-testid="private-investment-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button 
            onClick={() => handleSave(true)} 
            disabled={saving || uploadingDoc} 
            className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
            data-testid="private-investment-save-close"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
            {saving ? 'Saving...' : 'Save & Close'}
          </Button>
        </div>
      </div>
    </div>
  );
});

export default PrivateInvestmentDetailModal;
